/**
 * @module MixedAxisConfig/useMixedMetricLinkage
 * @description 组合图 · 指标单选互补联动
 */

import { useCallback } from 'react';
import type { IMixedAxisConfig, TAxisKey } from '../common/DualAxis/types';

interface IUseMixedMetricLinkageParams {
  primaryAxis: IMixedAxisConfig;
  secondaryAxis: IMixedAxisConfig;
  metricValues: string[];
  getMetricByUid: (uid: string) => string;
  dispatchAxes: (payload: Partial<Record<TAxisKey, IMixedAxisConfig>>) => void;
}

/** 根据 metric 字符串反查 uid（用于 Select value） */
export function resolveMetricUid(
  metric: string | undefined,
  metricValues: string[],
  getMetricUid: (metric: string, tableCode?: string) => string,
): string {
  if (!metric) return '';
  const matched = metricValues.find((uid) => {
    const parts = uid.split('@@DATALAB@@');
    return parts[0] === metric;
  });
  return matched || getMetricUid(metric);
}

export function useMixedMetricLinkage({
  primaryAxis,
  secondaryAxis,
  metricValues,
  getMetricByUid,
  dispatchAxes,
}: IUseMixedMetricLinkageParams) {
  return useCallback(
    (axisKey: TAxisKey, selectedMetric: string) => {
      const otherKey: TAxisKey =
        axisKey === 'primaryYAxis' ? 'secondYAxis' : 'primaryYAxis';
      const currentAxis =
        axisKey === 'primaryYAxis' ? primaryAxis : secondaryAxis;
      const otherAxis =
        axisKey === 'primaryYAxis' ? secondaryAxis : primaryAxis;
      const allMetrics = metricValues.map((uid) => getMetricByUid(uid));
      const otherMetric =
        allMetrics.find((m) => m && m !== selectedMetric) || '';

      dispatchAxes({
        [axisKey]: {
          ...currentAxis,
          metric: selectedMetric,
        },
        [otherKey]: {
          ...otherAxis,
          metric: otherMetric,
        },
      });
    },
    [
      dispatchAxes,
      getMetricByUid,
      metricValues,
      primaryAxis,
      secondaryAxis,
    ],
  );
}
